/**
 * Canvas Utilities
 *
 * Functions for applying filters, overlays, and cropping to images
 * using the Canvas API, then exporting the result as a blob
 */

import type { Area } from 'react-easy-crop'
import type { FilterSettings, OverlayType } from './filterPresets'
import { filterSettingsToCSS, getOverlayConfig } from './filterPresets'

/**
 * Load an image from URL with CORS enabled
 *
 * NOTE: Requires the CDN/backend to return proper CORS headers:
 * Access-Control-Allow-Origin: * (or specific origin)
 *
 * Without CORS headers, canvas.toBlob() will fail with a security error.
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (error) => reject(error)
    img.src = url
  })
}

/**
 * Create a rotated canvas
 */
function getRotatedDimensions(
  width: number,
  height: number,
  rotation: number
): { width: number; height: number } {
  const radians = (rotation * Math.PI) / 180
  const sin = Math.abs(Math.sin(radians))
  const cos = Math.abs(Math.cos(radians))

  return {
    width: Math.round(width * cos + height * sin),
    height: Math.round(width * sin + height * cos),
  }
}

/**
 * Apply rotation to canvas context
 */
function applyRotation(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  rotation: number
): void {
  ctx.translate(width / 2, height / 2)
  ctx.rotate((rotation * Math.PI) / 180)
  ctx.translate(-width / 2, -height / 2)
}

/**
 * Draw gradient overlay on canvas
 */
function drawOverlay(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  overlay: OverlayType
): void {
  const config = getOverlayConfig(overlay)
  if (overlay === 'none' || config.opacity === 0) return

  ctx.save()
  ctx.globalCompositeOperation = config.blendMode as GlobalCompositeOperation
  ctx.globalAlpha = config.opacity

  // Parse and draw the gradient
  const gradient = parseGradient(ctx, width, height, config.css)
  if (gradient) {
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
  }

  ctx.restore()
}

/**
 * Parse CSS gradient string to CanvasGradient
 * Supports basic linear-gradient and radial-gradient
 */
function parseGradient(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  cssGradient: string
): CanvasGradient | null {
  // Handle radial gradients (vignettes)
  if (cssGradient.startsWith('radial-gradient')) {
    const gradient = ctx.createRadialGradient(
      width / 2,
      height / 2,
      0,
      width / 2,
      height / 2,
      Math.max(width, height) / 2
    )

    // Parse color stops from CSS
    const colorStops = parseColorStops(cssGradient)
    colorStops.forEach(({ offset, color }) => {
      gradient.addColorStop(offset, color)
    })

    return gradient
  }

  // Handle linear gradients
  if (cssGradient.startsWith('linear-gradient')) {
    // Extract angle (default 180deg = top to bottom)
    const angleMatch = cssGradient.match(/(\d+)deg/)
    const angle = angleMatch ? parseInt(angleMatch[1]) : 180

    // Calculate gradient line based on angle
    const radians = ((angle - 90) * Math.PI) / 180
    const x1 = width / 2 - (Math.cos(radians) * width) / 2
    const y1 = height / 2 - (Math.sin(radians) * height) / 2
    const x2 = width / 2 + (Math.cos(radians) * width) / 2
    const y2 = height / 2 + (Math.sin(radians) * height) / 2

    const gradient = ctx.createLinearGradient(x1, y1, x2, y2)

    const colorStops = parseColorStops(cssGradient)
    colorStops.forEach(({ offset, color }) => {
      gradient.addColorStop(offset, color)
    })

    return gradient
  }

  return null
}

/**
 * Parse color stops from CSS gradient string
 */
function parseColorStops(
  cssGradient: string
): Array<{ offset: number; color: string }> {
  const stops: Array<{ offset: number; color: string }> = []

  // Match rgba() or transparent with percentage
  const regex = /(rgba?\([^)]+\)|transparent)\s*(\d+)?%?/g
  let match
  let index = 0

  while ((match = regex.exec(cssGradient)) !== null) {
    const color = match[1]
    const percentage = match[2] ? parseInt(match[2]) / 100 : index === 0 ? 0 : 1
    stops.push({ offset: percentage, color })
    index++
  }

  // If no stops found, return transparent
  if (stops.length === 0) {
    stops.push({ offset: 0, color: 'transparent' })
    stops.push({ offset: 1, color: 'transparent' })
  }

  return stops
}

export interface ExportOptions {
  /** Crop area in pixels */
  cropArea: Area
  /** Rotation in degrees */
  rotation: number
  /** Filter settings */
  filters: FilterSettings
  /** Overlay type */
  overlay: OverlayType
  /** Output format */
  format?: 'image/jpeg' | 'image/png' | 'image/webp'
  /** Output quality (0-1, for jpeg/webp) */
  quality?: number
  /** Maximum output dimension (preserves aspect ratio) */
  maxDimension?: number
}

/**
 * Export an edited image as a Blob
 *
 * Applies all transformations:
 * 1. Crop to specified area
 * 2. Apply rotation
 * 3. Apply CSS filters
 * 4. Apply overlay
 * 5. Export as blob
 */
export async function exportEditedImage(
  imageUrl: string,
  options: Omit<ExportOptions, 'zoom'> & { zoom?: number }
): Promise<Blob> {
  const {
    cropArea,
    rotation,
    filters,
    overlay,
    format = 'image/jpeg',
    quality = 0.95, // High quality for print production
    maxDimension, // No limit by default - preserve original resolution
  } = options

  // Load the original image
  const image = await loadImage(imageUrl)

  // Calculate output dimensions
  let outputWidth = cropArea.width
  let outputHeight = cropArea.height

  // Scale down if maxDimension is set and image exceeds it
  if (
    maxDimension &&
    (outputWidth > maxDimension || outputHeight > maxDimension)
  ) {
    const scale = maxDimension / Math.max(outputWidth, outputHeight)
    outputWidth = Math.round(outputWidth * scale)
    outputHeight = Math.round(outputHeight * scale)
  }

  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = outputWidth
  canvas.height = outputHeight
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to get canvas context')
  }

  // Apply CSS filter
  const filterCSS = filterSettingsToCSS(filters)
  ctx.filter = filterCSS

  // If rotation is needed, handle it
  if (rotation !== 0) {
    // For non-zero rotation, we need a more complex approach
    // Create a temporary canvas for the rotated source
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    if (!tempCtx) throw new Error('Failed to get temp canvas context')

    const rotatedDims = getRotatedDimensions(
      image.width,
      image.height,
      rotation
    )
    tempCanvas.width = rotatedDims.width
    tempCanvas.height = rotatedDims.height

    applyRotation(tempCtx, rotatedDims.width, rotatedDims.height, rotation)

    // Draw original image centered
    const offsetX = (rotatedDims.width - image.width) / 2
    const offsetY = (rotatedDims.height - image.height) / 2
    tempCtx.drawImage(image, offsetX, offsetY)

    // Now draw the cropped area from the rotated image
    ctx.drawImage(
      tempCanvas,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      outputWidth,
      outputHeight
    )
  } else {
    // No rotation - direct crop
    ctx.drawImage(
      image,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      outputWidth,
      outputHeight
    )
  }

  // Reset filter for overlay
  ctx.filter = 'none'

  // Apply overlay
  drawOverlay(ctx, outputWidth, outputHeight, overlay)

  // Export as blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to export canvas to blob'))
        }
      },
      format,
      quality
    )
  })
}

/**
 * Convert blob to File object
 */
export function blobToFile(blob: Blob, filename: string): File {
  return new File([blob], filename, { type: blob.type })
}
